import React from "react";

import * as Pages from "@/components/Pages";
import * as PageOptions from "@/components/PaperContainer/PageOptions";
import * as Spaces from "@/models/spaces";

import {
  AvatarList,
  DangerButton,
  IconPencil,
  IconSettings,
  IconTrash,
  Modal,
  PrimaryButton,
  SecondaryButton,
  showErrorToast,
  showSuccessToast,
  WarningCallout,
} from "turboui";

import { useClearNotificationsOnLoad } from "@/features/notifications";
import { PrivacyIndicator } from "@/features/spaces/PrivacyIndicator";
import { useJoinSpace } from "@/models/spaces";
import { assertPresent } from "@/utils/assertions";

import { usePaths } from "@/routes/paths";
import { useNavigate } from "react-router-dom";
import { match } from "ts-pattern";
import { useLoadedData, useRefresh } from "./loader";
import { SpaceBento } from "./Bento";
import { useReloadFeedSignal, publish, LocalSignal } from "@/signals";

//
// Single-space page — "bento dashboard" direction.
//
// The persistent quick-switch Spaces rail is provided by SpacesRailLayout; this
// page renders only the canvas: a left-aligned header band followed by the bento
// grid of tool tiles (Goals & Projects, Activity, Discussions, Documents,
// Tasks).
//
export function Page() {
  const { space, tools } = useLoadedData();
  const refresh = useRefresh();

  useClearNotificationsOnLoad(space.notifications || []);

  // Live dashboard. Any activity in this space (a goal / task / discussion /
  // document / contact change) broadcasts api:reload_feed:<space_id>. On that
  // signal we re-run the page loader — refreshing every tile fed by
  // listSpaceTools — and fan out a local signal so the tiles that own their own
  // query (Activity feed, Contacts) refetch in place. No manual reload needed.
  const onSpaceActivity = React.useCallback(() => {
    refresh();
    publish(LocalSignal.RefreshSpaceData);
  }, [refresh]);

  const feedPayload = React.useMemo(() => ({ resourceId: space.id! }), [space.id]);
  useReloadFeedSignal(onSpaceActivity, feedPayload);

  return (
    <Pages.Page title={space.name!} testId="space-page">
      <div className="relative px-5 sm:px-8 lg:px-10 py-8 w-full max-w-[1600px]">
        <SpaceOptions />
        <SpaceHeaderBand space={space} />
        <JoinButton space={space} />
        <SpaceBento space={space} tools={tools} />
      </div>
    </Pages.Page>
  );
}

function SpaceHeaderBand({ space }: { space: Spaces.Space }) {
  const size = Pages.useWindowSizeBreakpoints();

  const peopleToShow = match(size)
    .with("xs", () => 5)
    .with("sm", () => 8)
    .otherwise(() => 12);

  const memberCount = space.members?.length ?? 0;

  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-bold text-2xl sm:text-3xl truncate">{space.name}</div>
          {space.accessLevels && <PrivacyIndicator space={space} size={22} />}
        </div>

        {space.mission && <div className="text-content-dimmed mt-1">{space.mission}</div>}

        <div className="flex items-center gap-2 mt-3" data-test-id="space-members">
          <AvatarList people={space.members!} stacked size="small" maxElements={peopleToShow} />
          <span className="text-xs text-content-subtle">
            {memberCount} {memberCount === 1 ? "member" : "members"}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <ManageAccessButton space={space} />
      </div>
    </div>
  );
}

function JoinButton({ space }: { space: Spaces.Space }) {
  const refresh = useRefresh();
  const [join] = useJoinSpace();

  if (space.isMember) return null;

  const handleClick = async () => {
    await join({ spaceId: space.id });
    refresh();
  };

  return (
    <div className="flex mb-6">
      <PrimaryButton size="sm" onClick={handleClick} testId="join-space-button">
        Join this Space
      </PrimaryButton>
    </div>
  );
}

function ManageAccessButton({ space }: { space: Spaces.Space }) {
  const paths = usePaths();
  const path = paths.spaceAccessManagementPath(space.id!);

  assertPresent(space.permissions, "permissions must be present in space");
  if (!space.permissions.hasFullAccess) return null;

  return (
    <SecondaryButton linkTo={path} size="xs" testId="access-management">
      Manage access
    </SecondaryButton>
  );
}

function SpaceOptions() {
  const { space, tools } = useLoadedData();
  const navigate = useNavigate();
  const [deleteSpace, { loading: isDeleting }] = Spaces.useDeleteSpace();
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const resourceCounts = React.useMemo(() => {
    const projectCount = tools.projects?.length ?? 0;
    const goalCount = tools.goals?.length ?? 0;
    const messageCount = (tools.messagesBoards ?? []).reduce((sum, board) => sum + (board.messages?.length ?? 0), 0);

    return { projectCount, goalCount, messageCount };
  }, [tools]);

  const hasSubresources = React.useMemo(() => {
    return Object.values(resourceCounts).some((count) => count > 0);
  }, [resourceCounts]);

  const paths = usePaths();

  const performDelete = React.useCallback(async () => {
    try {
      await deleteSpace({ spaceId: space.id });
      showSuccessToast("Space deleted", "The space and its content were deleted.");
      navigate(paths.homePath());
    } catch (error) {
      console.error("Failed to delete space", error);
      showErrorToast("Failed to delete space", "Please try again.");
      throw error;
    }
  }, [deleteSpace, navigate, paths, space.id]);

  const handleDelete = React.useCallback(() => {
    if (isDeleting) return;

    if (hasSubresources) {
      setIsModalOpen(true);
      return;
    }

    performDelete();
  }, [hasSubresources, isDeleting, performDelete]);

  const handleConfirmDelete = React.useCallback(async () => {
    try {
      await performDelete();
      setIsModalOpen(false);
    } catch (error) {
      // Error toast already shown in performDelete; keep modal open for another attempt.
    }
  }, [performDelete]);

  const handleCloseModal = React.useCallback(() => {
    if (!isDeleting) {
      setIsModalOpen(false);
    }
  }, [isDeleting]);

  const editLink = paths.spaceEditPath(space.id!);
  const toolsConfigLink = paths.spaceToolsConfigPath(space.id!);

  return (
    <>
      <PageOptions.Root testId="options-button">
        {space.permissions?.canEdit && (
          <PageOptions.Link keepOutsideOnBigScreen icon={IconPencil} to={editLink} title="Edit" testId="edit-space" />
        )}
        {space.permissions?.canEdit && (
          <PageOptions.Link icon={IconSettings} to={toolsConfigLink} title="Configure tools" testId="configure-tools" />
        )}
        {space.permissions?.hasFullAccess && !space.isCompanySpace && (
          <PageOptions.Action icon={IconTrash} title="Delete" onClick={handleDelete} testId="delete-space" />
        )}
      </PageOptions.Root>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Delete space"
        size="large"
        contentPadding="p-6"
        closeOnBackdropClick={!isDeleting}
      >
        <div className="space-y-4">
          <WarningCallout
            message="This action cannot be undone."
            description="Deleting this space will permanently remove everything in it. This includes all projects, goals, and discussions."
          />

          <div className="flex justify-end gap-3">
            <SecondaryButton size="sm" onClick={handleCloseModal} disabled={isDeleting}>
              Cancel
            </SecondaryButton>
            <DangerButton size="sm" onClick={handleConfirmDelete} loading={isDeleting} testId="confirm-delete-space">
              Delete everything
            </DangerButton>
          </div>
        </div>
      </Modal>
    </>
  );
}
